import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle, Gift } from 'lucide-react';
import { validateInviteCode } from '../Services/AdminService';

// // Mock API Call - Replace with your actual service
// const validateInviteCode = async (code: string) => {
//   return new Promise((resolve) => setTimeout(() => resolve(true), 2000));
// };

const InvitationGateComponent: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');


  const hasValidated = useRef(false);

  useEffect(() => {
     if (hasValidated.current) return;

     hasValidated.current = true;
     
    const checkInvite = async () => {
      if (!inviteCode) {
        setStatus('error');
        return;
      }

      try {
        const response = await validateInviteCode(inviteCode);
        if (response.success) {
          setStatus('success');
          // Redirect to registration/login after a brief delay to show success
          setTimeout(() => navigate('/registration/'+inviteCode), 5000);
        } else {
          setStatus('error');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    checkInvite();
  }, [inviteCode]);

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#0a0a0a] px-4">
      {/* Animated Mesh Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-purple-600/30 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-600/20 blur-[120px]" />
        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-pink-500/20 blur-[100px]" />
      </div>

      {/* Main Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl text-center">
          
          <AnimatePresence mode="wait">
            {status === 'loading' && (
              <motion.div
                key="loading"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.1, opacity: 0 }}
                className="space-y-6"
              >
                <div className="relative mx-auto w-20 h-20">
                  <div className="absolute inset-0 rounded-full border-t-2 border-purple-500 animate-spin" />
                  <div className="flex items-center justify-center h-full">
                    <Gift className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-white tracking-tight">Validating Invite</h2>
                  <p className="text-gray-400 text-sm">Checking your exclusive access code...</p>
                </div>
              </motion.div>
            )}

            {status === 'success' && (
              <motion.div
                key="success"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="space-y-6"
              >
                <div className="mx-auto w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-white tracking-tight">Welcome to the Club</h2>
                  <p className="text-emerald-400/80 text-sm font-medium">Redirecting you to complete registration</p>
                </div>
              </motion.div>
            )}

            {status === 'error' && (
              <motion.div
                key="error"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="space-y-6"
              >
                <div className="mx-auto w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-white tracking-tight">Invalid Invite</h2>
                  <p className="text-gray-400 text-sm">This code has expired or is incorrect.</p>
                </div>
                <button 
                  onClick={() => navigate('/login')}
                  className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold hover:opacity-90 transition-opacity"
                >
                  Go to Login
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer Branding */}
          <div className="mt-8 pt-8 border-t border-white/5">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">
              Premium Executive Access
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default InvitationGateComponent;